import { Fragment, type ReactNode } from 'react';

/**
 * Мини-парсер markdown для ответов LLM (виджет и тест-чат кабинета).
 * Без зависимостей: виджет собирается отдельным бандлом и импортирует файл напрямую.
 *
 * Inline:
 *   - **bold** и __bold__
 *   - *italic* и _italic_ (с word-boundary)
 *   - `inline code`
 *   - [text](url) — только http/https/mailto
 *
 * Block:
 *   - ## H2  и  ### H3 (H1 не рендерим — слишком крупно)
 *   - параграфы (двойной \n)
 *   - одиночный \n → <br/>
 *   - маркированные списки: "- item" или "* item"
 *   - нумерованные списки: "1. item", "2. item", ...
 *
 * НЕ поддерживает: H1/H4-H6, таблицы, блоки кода (```), цитаты (>),
 * вложенные списки. ~140 строк, ~2КБ в бандле.
 *
 * Безопасность: XSS невозможен — все узлы создаются как React-узлы,
 * никакого dangerouslySetInnerHTML. Ссылки фильтруются по схеме.
 */

interface MarkdownProps {
  text: string;
}

const SAFE_URL_RE = /^(https?:|mailto:)/i;

function isSafeUrl(url: string): boolean {
  return SAFE_URL_RE.test(url);
}

// === Inline =============================================================

function parseInline(text: string): ReactNode[] {
  const tokenRe =
    /`([^`\n]+?)`|\[([^\]\n]+?)\]\(([^)\s]+?)\)|\*\*([^*\n]+?)\*\*|__([^_\n]+?)__|(?<![\w\d])\*([^*\n]+?)\*(?![\w\d])|(?<![\w\d])_([^_\n]+?)_(?![\w\d])/g;

  const nodes: ReactNode[] = [];
  let lastIndex = 0;
  let m: RegExpExecArray | null;
  let key = 0;

  while ((m = tokenRe.exec(text)) !== null) {
    if (m.index > lastIndex) nodes.push(text.slice(lastIndex, m.index));
    if (m[1] !== undefined) {
      nodes.push(<code key={key++}>{m[1]}</code>);
    } else if (m[2] !== undefined && m[3] !== undefined) {
      const linkText = m[2];
      const url = m[3];
      if (isSafeUrl(url)) {
        nodes.push(
          <a key={key++} href={url} target="_blank" rel="noopener noreferrer">
            {linkText}
          </a>,
        );
      } else {
        nodes.push(m[0]);
      }
    } else if (m[4] !== undefined) {
      nodes.push(<strong key={key++}>{m[4]}</strong>);
    } else if (m[5] !== undefined) {
      nodes.push(<strong key={key++}>{m[5]}</strong>);
    } else if (m[6] !== undefined) {
      nodes.push(<em key={key++}>{m[6]}</em>);
    } else if (m[7] !== undefined) {
      nodes.push(<em key={key++}>{m[7]}</em>);
    }
    lastIndex = m.index + m[0].length;
  }
  if (lastIndex < text.length) nodes.push(text.slice(lastIndex));
  return nodes;
}

// === Блочный рендер =====================================================

function renderBlock(blockLines: string[], key: number): ReactNode {
  if (blockLines.length === 0) return null;
  const first = blockLines[0];

  // ### H3
  if (first.startsWith('### ')) {
    return (
      <h3 key={key} className="rch-md-h3">
        {parseInline(first.slice(4))}
      </h3>
    );
  }
  // ## H2
  if (first.startsWith('## ')) {
    return (
      <h2 key={key} className="rch-md-h2">
        {parseInline(first.slice(3))}
      </h2>
    );
  }
  // # H1 — не рендерим как заголовок, пусть идёт как обычный параграф
  //         (визуально слишком большой для bubble).

  // Маркированный список: "- item" или "* item"
  const bulletRe = /^[-*]\s+(.+)$/;
  const isBulletList = blockLines.every((line) => bulletRe.test(line));
  if (isBulletList) {
    return (
      <ul key={key} className="rch-md-ul">
        {blockLines.map((line, idx) => {
          const m = line.match(bulletRe);
          return <li key={idx}>{m ? parseInline(m[1]) : line}</li>;
        })}
      </ul>
    );
  }

  // Нумерованный список: "1. ", "2. ", ...
  const numberedRe = /^\d+\.\s+(.+)$/;
  const isNumberedList = blockLines.every((line) => numberedRe.test(line));
  if (isNumberedList) {
    return (
      <ol key={key} className="rch-md-ol">
        {blockLines.map((line, idx) => {
          const m = line.match(numberedRe);
          return <li key={idx}>{m ? parseInline(m[1]) : line}</li>;
        })}
      </ol>
    );
  }

  // Обычный параграф; внутри \n → <br/>.
  return (
    <p key={key}>
      {blockLines.map((line, idx) => (
        <Fragment key={idx}>
          {parseInline(line)}
          {idx < blockLines.length - 1 && <br />}
        </Fragment>
      ))}
    </p>
  );
}

/**
 * Группирует строки в блоки:
 *   - Пустая строка = граница блока
 *   - Заголовки (### ## #) — самостоятельные блоки, даже без переноса
 */
function groupBlocks(text: string): string[][] {
  const normalized = text.replace(/\r\n/g, '\n');
  const lines = normalized.split('\n');

  const blocks: string[][] = [];
  let current: string[] = [];

  function flush() {
    if (current.length > 0) {
      blocks.push(current);
      current = [];
    }
  }

  for (const rawLine of lines) {
    const line = rawLine.trimEnd();
    if (line === '') {
      flush();
      continue;
    }
    if (/^#{1,3}\s/.test(line)) {
      flush();
      blocks.push([line]);
      continue;
    }
    current.push(line);
  }
  flush();
  return blocks;
}

// === Склеивание соседних блоков-списков ====================================
//
// LLM часто разбивает пункты списка пустыми строками:
//   1. шаг один
//
//   2. шаг два
//
//   3. шаг три
//
// groupBlocks возвращает на это 3 отдельных блока. Каждый — один <ol> с
// одним <li>, в результате браузер показывает "1. 1. 1." вместо "1. 2. 3.".
//
// Решение: после groupBlocks склеиваем соседние блоки одного списка-типа
// в один блок. Это даёт один <ol> со всеми пунктами и правильную нумерацию.

const NUMBERED_RE = /^\d+\.\s+/;
const BULLET_RE = /^[-*]\s+/;

type ListKind = 'numbered' | 'bullet' | null;

function listKind(blockLines: string[]): ListKind {
  if (blockLines.length === 0) return null;
  if (blockLines.every((l) => NUMBERED_RE.test(l))) return 'numbered';
  if (blockLines.every((l) => BULLET_RE.test(l))) return 'bullet';
  return null;
}

function mergeListBlocks(blocks: string[][]): string[][] {
  const out: string[][] = [];
  for (const block of blocks) {
    const kind = listKind(block);
    if (kind === null) {
      out.push(block);
      continue;
    }
    // Если предыдущий блок — список того же типа, склеиваем в один.
    const prev = out[out.length - 1];
    if (prev && listKind(prev) === kind) {
      prev.push(...block);
    } else {
      out.push([...block]);
    }
  }
  return out;
}

// === Корневой компонент ==================================================

export function Markdown({ text }: MarkdownProps) {
  const blocks = mergeListBlocks(groupBlocks(text));
  return <>{blocks.map((block, idx) => renderBlock(block, idx))}</>;
}
